import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TipTapImage from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { Alert, Button } from 'reactstrap';
import { NavMenu } from '../../navMenu/NavMenu';
import { ApiEndpoints } from '../../../api/apiEndpoints';

// ─── Editor CSS (injected once) ───────────────────────────────────────────────
const EDITOR_CSS = `
.tiptap-wrap .ProseMirror {
  min-height: 150px;
  padding: 12px 16px;
  outline: none;
  line-height: 1.7;
  font-size: 15px;
  background: #fff;
  border-radius: 0 0 6px 6px;
}
.tiptap-wrap .ProseMirror:focus {
  box-shadow: inset 0 0 0 2px rgba(74,144,226,0.15);
}
.tiptap-wrap .ProseMirror > * + * { margin-top: 0.75em; }
.tiptap-wrap .ProseMirror h1 { font-size: 2em; font-weight: bold; margin-top: 0.5em; margin-bottom: 0.5em; }
.tiptap-wrap .ProseMirror h2 { font-size: 1.5em; font-weight: bold; margin-top: 0.5em; margin-bottom: 0.5em; }
.tiptap-wrap .ProseMirror h3 { font-size: 1.25em; font-weight: bold; margin-top: 0.5em; margin-bottom: 0.5em; }
.tiptap-wrap .ProseMirror table {
  border-collapse: collapse;
  width: 100%;
  margin: 12px 0;
  table-layout: fixed;
  overflow: hidden;
}
.tiptap-wrap .ProseMirror td,
.tiptap-wrap .ProseMirror th {
  border: 1px solid #ddd;
  padding: 8px 12px;
  min-width: 60px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}
.tiptap-wrap .ProseMirror th { 
  background: #f8f9fa; 
  font-weight: 600;
  color: #495057;
}
.tiptap-wrap .ProseMirror blockquote {
  border-left: 4px solid #4a90e2;
  padding-left: 16px;
  margin-left: 0;
  color: #666;
  font-style: italic;
  background: #f8f9fa;
  padding-top: 8px;
  padding-bottom: 8px;
  border-radius: 0 4px 4px 0;
}
.tiptap-wrap .ProseMirror ul { list-style: disc; padding-left: 1.8em; margin: 0.5em 0; }
.tiptap-wrap .ProseMirror ol { list-style: decimal; padding-left: 1.8em; margin: 0.5em 0; }
.tiptap-wrap .ProseMirror li { margin: 0.25em 0; }
.tiptap-wrap .ProseMirror img {
  max-width: 100%;
  height: auto;
  cursor: pointer;
  display: block;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: box-shadow 0.2s;
}
.tiptap-wrap .ProseMirror img:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.tiptap-wrap .ProseMirror a { 
  color: #4a90e2; 
  text-decoration: underline;
  transition: color 0.2s;
}
.tiptap-wrap .ProseMirror a:hover {
  color: #2c6bb8;
}
.tiptap-wrap .ProseMirror .selectedNode { 
  outline: 2px solid #4a90e2;
  box-shadow: 0 0 0 4px rgba(74,144,226,0.1);
}
.tiptap-wrap .ProseMirror p.is-editor-empty:first-child::before {
  color: #adb5bd;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

/* Responsive styles for editor columns */
@media (max-width: 768px) {
  .tiptap-wrap .ProseMirror {
    font-size: 14px;
    padding: 10px 12px;
  }
  .tiptap-wrap .ProseMirror img {
    float: none !important;
    margin: 8px auto !important;
    max-width: 100% !important;
  }
}
`;

let cssInjected = false;
function injectEditorCss() {
    if (cssInjected) return;
    cssInjected = true;
    const style = document.createElement('style');
    style.textContent = EDITOR_CSS;
    document.head.appendChild(style);
}

// ─── Custom Image extension (adds 'style' attribute for float + resize) ───────
const CustomImage = TipTapImage.extend({
    selectable: true,
    draggable: false,
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (el) => el.getAttribute('style') || null,
                renderHTML: (attrs) => attrs.style ? { style: attrs.style } : {},
            },
        };
    },
});

// ─── Build common extensions ──────────────────────────────────────────────────
function buildExtensions() {
    return [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        Underline,
        Link.configure({ openOnClick: false, autolink: true }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        CustomImage.configure({ inline: false, allowBase64: false }),
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
    ];
}

// ─── Upload helper ────────────────────────────────────────────────────────────
async function uploadImage(file) {
    const fd = new FormData();
    fd.append('image', file);
    const res = await fetch(ApiEndpoints.ManageAboutPageImages, { method: 'POST', body: fd });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
    const { url } = await res.json();
    return url;
}

function triggerImageUpload(onUrl) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif';
    input.click();
    input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try { onUrl(await uploadImage(file)); }
        catch (e) { alert('Image upload failed: ' + e.message); }
    };
}

// ─── ImageOverlay: float bar + resize handle ──────────────────────────────────
function ImageOverlay({ editor, imgEl, containerEl }) {
    // Keep a ref so every callback always uses the freshest DOM element,
    // even after ProseMirror recreates the <img> node on setNodeMarkup.
    const imgElRef = useRef(imgEl);
    useEffect(() => { imgElRef.current = imgEl; }, [imgEl]);

    const [rect, setRect] = useState(null);

    // updateRect only closes over containerEl (stable); reads imgEl from the ref.
    const updateRect = useCallback(() => {
        const el = imgElRef.current;
        if (!el || !containerEl) return;
        const cr = containerEl.getBoundingClientRect();
        const ir = el.getBoundingClientRect();
        // If the element is detached, getBoundingClientRect returns all zeros —
        // ignore that update to avoid snapping the overlay to (0,0).
        if (!ir.width && !ir.height) return;
        setRect({ left: ir.left - cr.left, top: ir.top - cr.top, width: ir.width, height: ir.height });
    }, [containerEl]);

    useLayoutEffect(() => {
        updateRect();
        window.addEventListener('resize', updateRect);
        return () => window.removeEventListener('resize', updateRect);
    }, [updateRect]);

    // Sync rect when the editor content changes (e.g. after resize commit)
    useEffect(() => {
        if (!editor) return;
        editor.on('update', updateRect);
        return () => editor.off('update', updateRect);
    }, [editor, updateRect]);

    const applyStyle = useCallback((newStyle) => {
        const el = imgElRef.current;
        if (!el) return;

        const cur = el.getAttribute('style') || '';
        const curWidth = (cur.match(/width:\s*[\d.]+px/) || [])[0];   // e.g. "width:200px"
        const curFloat = (cur.match(/float:\s*(?:left|right|none)/) || [])[0]; // e.g. "float:left"
        const newHasFloat = /float:/.test(newStyle);
        const newHasWidth = /width:\s*[\d.]+px/.test(newStyle);

        let merged = newStyle;

        if (newHasFloat && !newHasWidth) {
            // Float change: if newStyle doesn't already carry a width,
            // preserve the existing explicit width or set a sensible default
            // (so the image doesn't stretch to 100% of the container).
            if (curWidth) {
                merged += ';' + curWidth;
            } else if (!/float:\s*none/.test(newStyle)) {
                // First float on an unsized image — cap at min(naturalWidth, 40% of container)
                const natural = el.naturalWidth || 0;
                const containerW = containerEl ? containerEl.getBoundingClientRect().width : 0;
                const maxW = containerW > 0 ? Math.round(containerW * 0.4) : 300;
                const w = natural > 0 ? Math.min(natural, maxW) : maxW;
                if (w > 0) merged += `;width:${w}px`;
            }
        }

        if (newHasWidth && !newHasFloat) {
            // Resize: new style carries width but no float → preserve existing float
            if (curFloat && curFloat !== 'float:none') {
                merged += ';' + curFloat;
            }
        }

        // Find the image node by walking the doc and matching its DOM element,
        // then dispatch setNodeMarkup directly — works regardless of selection.
        let foundPos = -1;
        editor.state.doc.descendants((node, pos) => {
            if (foundPos >= 0) return false;
            if (node.type.name === 'image' && editor.view.nodeDOM(pos) === el) {
                foundPos = pos;
                return false;
            }
        });
        if (foundPos < 0) return;

        const targetNode = editor.state.doc.nodeAt(foundPos);
        editor.view.dispatch(
            editor.state.tr.setNodeMarkup(foundPos, null, { ...targetNode.attrs, style: merged })
        );

        // Double rAF: wait for ProseMirror to flush its DOM update, then
        // re-acquire the (possibly recreated) <img> element and refresh the rect.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const freshEl = editor.view.nodeDOM(foundPos);
            if (freshEl instanceof Element) imgElRef.current = freshEl;
            updateRect();
        }));
    }, [editor, containerEl, updateRect]);

    const setFloat = (dir) => {
        if (dir === 'left')  applyStyle('float:left;margin:0 14px 8px 0;height:auto;');
        else if (dir === 'right') applyStyle('float:right;margin:0 0 8px 14px;height:auto;');
        else applyStyle('display:block;margin:8px auto;float:none;height:auto;');
    };

    const startResize = (e) => {
        e.preventDefault(); e.stopPropagation();
        const startX = e.clientX;
        const startW = imgElRef.current?.getBoundingClientRect().width ?? 0;
        const onMove = (ev) => {
            const el = imgElRef.current;
            if (!el) return;
            const w = Math.max(50, startW + (ev.clientX - startX));
            el.style.width = `${w}px`;
            el.style.height = 'auto';
            updateRect();
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            const el = imgElRef.current;
            if (!el) return;
            const w = Math.round(el.getBoundingClientRect().width);
            applyStyle(`width:${w}px;height:auto;`);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    if (!rect) return null;

    const btnStyle = {
        padding: '2px 8px', fontSize: 11, lineHeight: '1.6',
        background: '#4a90e2', color: '#fff',
        border: 'none', borderRadius: 3, cursor: 'pointer',
    };

    return (
        <div style={{
            position: 'absolute', pointerEvents: 'none', boxSizing: 'border-box', zIndex: 10,
            left: rect.left, top: rect.top, width: rect.width, height: rect.height,
            border: '2px dashed #4a90e2',
        }}>
            {/* Float bar */}
            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, display: 'flex', gap: 3, pointerEvents: 'all', zIndex: 12 }}>
                {[['⬅ Float left', 'left'], ['⊝ Center', null], ['Float right ➡', 'right']].map(([label, dir]) => (
                    <button key={label} style={btnStyle}
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setFloat(dir); }}>
                        {label}
                    </button>
                ))}
            </div>
            {/* Resize handle */}
            <div onMouseDown={startResize} style={{
                position: 'absolute', right: -6, bottom: -6,
                width: 12, height: 12, background: '#4a90e2',
                border: '2px solid #fff', borderRadius: '50%',
                cursor: 'se-resize', pointerEvents: 'all', zIndex: 11,
            }} />
        </div>
    );
}

// ─── Toolbar primitives (defined OUTSIDE Toolbar so type ref is stable) ───────
const tbBtnStyle = {
    background: '#fff',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    padding: '6px 10px',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '1.4',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s ease',
    minWidth: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const tbBtnActiveStyle = { 
    ...tbBtnStyle, 
    background: '#4a90e2', 
    borderColor: '#4a90e2',
    color: '#fff',
    boxShadow: '0 0 0 3px rgba(74,144,226,0.1)',
};

const tbBtnHoverStyle = {
    background: '#f8f9fa',
    borderColor: '#adb5bd',
};

function TbBtn({ active, title, onClick, children }) {
    const [hover, setHover] = useState(false);
    
    const getStyle = () => {
        if (active) return tbBtnActiveStyle;
        if (hover) return { ...tbBtnStyle, ...tbBtnHoverStyle };
        return tbBtnStyle;
    };
    
    return (
        <button 
            title={title}
            onMouseDown={(e) => { e.preventDefault(); onClick(); }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={getStyle()}>
            {children}
        </button>
    );
}

function TbSep() {
    return <span style={{ 
        borderLeft: '1px solid #dee2e6', 
        margin: '0 6px', 
        alignSelf: 'stretch',
        height: '24px',
    }} />;
}

function TbGroup({ children }) {
    return (
        <div style={{ 
            display: 'flex', 
            gap: '4px',
            padding: '2px',
            background: '#fff',
            borderRadius: '6px',
            border: '1px solid #e9ecef',
        }}>
            {children}
        </div>
    );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────
function Toolbar({ editor, onImageUpload, showTable = true }) {
    if (!editor) return null;

    const inTable = editor.isActive('table');

    return (
        <div style={{
            display: 'flex', 
            flexWrap: 'wrap', 
            alignItems: 'center', 
            gap: '8px',
            padding: '10px 12px', 
            background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
            borderBottom: '1px solid #dee2e6',
            borderRadius: '6px 6px 0 0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            {/* Text formatting group */}
            <TbGroup>
                <TbBtn active={editor.isActive('bold')} title="Bold (Ctrl+B)" onClick={() => editor.chain().focus().toggleBold().run()}>
                    <b>B</b>
                </TbBtn>
                <TbBtn active={editor.isActive('italic')} title="Italic (Ctrl+I)" onClick={() => editor.chain().focus().toggleItalic().run()}>
                    <i>I</i>
                </TbBtn>
                <TbBtn active={editor.isActive('underline')} title="Underline (Ctrl+U)" onClick={() => editor.chain().focus().toggleUnderline().run()}>
                    <u>U</u>
                </TbBtn>
                <TbBtn active={editor.isActive('strike')} title="Strikethrough" onClick={() => editor.chain().focus().toggleStrike().run()}>
                    <s>S</s>
                </TbBtn>
            </TbGroup>

            {/* Headings group */}
            <TbGroup>
                {[1, 2, 3].map(n => (
                    <TbBtn key={n} active={editor.isActive('heading', { level: n })} title={`Heading ${n}`}
                        onClick={() => editor.chain().focus().toggleHeading({ level: n }).run()}>
                        H{n}
                    </TbBtn>
                ))}
                <TbBtn active={editor.isActive('paragraph')} title="Normal text"
                    onClick={() => editor.chain().focus().setParagraph().run()}>P</TbBtn>
            </TbGroup>

            {/* Alignment group */}
            <TbGroup>
                <TbBtn active={editor.isActive({ textAlign: 'left' })} title="Align left" 
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}>⬅</TbBtn>
                <TbBtn active={editor.isActive({ textAlign: 'center' })} title="Align center" 
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}>≡</TbBtn>
                <TbBtn active={editor.isActive({ textAlign: 'right' })} title="Align right" 
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}>➡</TbBtn>
                <TbBtn active={editor.isActive({ textAlign: 'justify' })} title="Align justify" 
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}>⬌</TbBtn>
            </TbGroup>

            {/* Lists and quote group */}
            <TbGroup>
                <TbBtn active={editor.isActive('bulletList')} title="Bullet list" 
                    onClick={() => editor.chain().focus().toggleBulletList().run()}>• —</TbBtn>
                <TbBtn active={editor.isActive('orderedList')} title="Ordered list" 
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</TbBtn>
                <TbBtn active={editor.isActive('blockquote')} title="Blockquote" 
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</TbBtn>
            </TbGroup>

            {/* Insert group */}
            <TbGroup>
                <TbBtn active={editor.isActive('link')} title={editor.isActive('link') ? 'Remove link' : 'Insert link'}
                    onClick={() => {
                        if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
                        const url = prompt('URL:');
                        if (url) editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
                    }}>🔗</TbBtn>
                <TbBtn active={false} title="Upload image" onClick={onImageUpload}>🖼</TbBtn>
            </TbGroup>

            {/* Table group */}
            {showTable && (
                <TbGroup>
                    <TbBtn active={false} title="Insert 3×3 table"
                        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run()}>
                        ⊞
                    </TbBtn>
                    {inTable && <>
                        <TbBtn active={false} title="Add row above" onClick={() => editor.chain().focus().addRowBefore().run()}>+R↑</TbBtn>
                        <TbBtn active={false} title="Add row below" onClick={() => editor.chain().focus().addRowAfter().run()}>+R↓</TbBtn>
                        <TbBtn active={false} title="Add col left" onClick={() => editor.chain().focus().addColumnBefore().run()}>+C←</TbBtn>
                        <TbBtn active={false} title="Add col right" onClick={() => editor.chain().focus().addColumnAfter().run()}>+C→</TbBtn>
                        <TbBtn active={false} title="Delete row" onClick={() => editor.chain().focus().deleteRow().run()}>−R</TbBtn>
                        <TbBtn active={false} title="Delete col" onClick={() => editor.chain().focus().deleteColumn().run()}>−C</TbBtn>
                        <TbBtn active={false} title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}>✕</TbBtn>
                    </>}
                </TbGroup>
            )}

            {/* Clear formatting */}
            <TbGroup>
                <TbBtn active={false} title="Clear formatting" 
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>✕</TbBtn>
            </TbGroup>
        </div>
    );
}

// ─── Hook: TipTap editor instance ─────────────────────────────────────────────
function useEditorInstance(content, onChangeFn) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const extensions = useMemo(() => buildExtensions(), []);

    // Initial content is frozen at mount time so the editor option never changes.
    const initialContentRef = useRef(content);

    // Always call the latest onChange without ever recreating the editor or
    // re-subscribing the listener (a new arrow function on every render would
    // make TipTap re-register its internal transaction handler each time).
    const onChangeFnRef = useRef(onChangeFn);
    onChangeFnRef.current = onChangeFn;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableOnUpdate = useMemo(() => ({ editor }) => {
        onChangeFnRef.current(editor.getHTML());
    }, []);

    return useEditor({
        extensions,
        content: initialContentRef.current || '<p></p>',
        onUpdate: stableOnUpdate,
    });
}

// ─── TextBlock ────────────────────────────────────────────────────────────────
function TextBlock({ block, onChange, onDelete, canDelete }) {
    const containerRef = useRef(null);
    const [selectedImg, setSelectedImg] = useState(null);

    const editor = useEditorInstance(block.html, (html) => onChange({ html }));

    const handleImageUpload = useCallback(() => {
        triggerImageUpload((url) => editor?.chain().focus().setImage({ src: url }).run());
    }, [editor]);

    // Close overlay when clicking outside image / overlay
    useEffect(() => {
        if (!selectedImg) return;
        const handler = (e) => {
            if (e.target === selectedImg) return;
            if (e.target.closest?.('[data-img-overlay]')) return;
            setSelectedImg(null);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [selectedImg]);

    const handleClick = useCallback((e) => {
        if (e.target.tagName === 'IMG') setSelectedImg(e.target);
    }, []);

    return (
        <div className="mb-2" style={{ position: 'relative' }}>
            <Toolbar editor={editor} onImageUpload={handleImageUpload} />
            <div
                className="tiptap-wrap"
                ref={containerRef}
                onClick={handleClick}
                style={{ position: 'relative', border: '1px solid #ccc', borderTop: 'none', borderRadius: '0 0 4px 4px' }}
            >
                <EditorContent editor={editor} />
                {selectedImg && containerRef.current && (
                    <div data-img-overlay="">
                        <ImageOverlay editor={editor} imgEl={selectedImg} containerEl={containerRef.current} />
                    </div>
                )}
            </div>
            {canDelete && (
                <Button size="sm" color="outline-danger"
                    style={{ position: 'absolute', top: 42, right: -40 }}
                    onClick={onDelete} title="Remove text block">✕</Button>
            )}
        </div>
    );
}

// ─── ColumnsBlock ─────────────────────────────────────────────────────────────
function ColumnsBlock({ block, onChange, onDelete }) {
    const [leftPct, setLeftPct] = useState(block.leftPct ?? 50);
    const containerRef  = useRef(null);
    const leftContRef   = useRef(null);
    const rightContRef  = useRef(null);
    const [leftImg,  setLeftImg]  = useState(null);
    const [rightImg, setRightImg] = useState(null);

    const leftEditor  = useEditorInstance(block.leftHtml,  (html) => onChange({ leftHtml: html }));
    const rightEditor = useEditorInstance(block.rightHtml, (html) => onChange({ rightHtml: html }));

    const handleLeftUpload  = useCallback(() => triggerImageUpload((url) => leftEditor?.chain().focus().setImage({ src: url }).run()),  [leftEditor]);
    const handleRightUpload = useCallback(() => triggerImageUpload((url) => rightEditor?.chain().focus().setImage({ src: url }).run()), [rightEditor]);

    const handleDividerDown = (e) => {
        e.preventDefault();
        const startX   = e.clientX;
        const startPct = leftPct;
        const totalW   = containerRef.current?.getBoundingClientRect().width ?? 1;
        const onMove   = (ev) => {
            const pct = Math.max(10, Math.min(90, startPct + ((ev.clientX - startX) / totalW) * 100));
            setLeftPct(pct);
            onChange({ leftPct: pct });
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // Dismiss overlay on outside click
    useEffect(() => {
        if (!leftImg && !rightImg) return;
        const handler = (e) => {
            if (e.target.tagName === 'IMG') return;
            if (e.target.closest?.('[data-img-overlay]')) return;
            setLeftImg(null); setRightImg(null);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [leftImg, rightImg]);

    return (
        <div className="mb-2" style={{ position: 'relative' }}>
            <div ref={containerRef} style={{ display: 'flex', border: '2px solid #28a745', borderRadius: 4, overflow: 'visible' }}>

                {/* Left column */}
                <div style={{ width: `${leftPct}%`, minWidth: '10%', flexShrink: 0 }}>
                    <Toolbar editor={leftEditor} onImageUpload={handleLeftUpload} showTable={false} />
                    <div className="tiptap-wrap" ref={leftContRef} onClick={(e) => { if (e.target.tagName === 'IMG') setLeftImg(e.target); }}
                        style={{ position: 'relative', border: '1px solid #ccc', borderTop: 'none' }}>
                        <EditorContent editor={leftEditor} />
                        {leftImg && leftContRef.current && (
                            <div data-img-overlay=""><ImageOverlay editor={leftEditor} imgEl={leftImg} containerEl={leftContRef.current} /></div>
                        )}
                    </div>
                </div>

                {/* Drag divider */}
                <div onMouseDown={handleDividerDown} title="Drag to resize columns"
                    style={{ width: 10, flexShrink: 0, background: '#28a745', cursor: 'col-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, userSelect: 'none' }}>⋮</div>

                {/* Right column */}
                <div style={{ flex: 1, minWidth: '10%' }}>
                    <Toolbar editor={rightEditor} onImageUpload={handleRightUpload} showTable={false} />
                    <div className="tiptap-wrap" ref={rightContRef} onClick={(e) => { if (e.target.tagName === 'IMG') setRightImg(e.target); }}
                        style={{ position: 'relative', border: '1px solid #ccc', borderTop: 'none' }}>
                        <EditorContent editor={rightEditor} />
                        {rightImg && rightContRef.current && (
                            <div data-img-overlay=""><ImageOverlay editor={rightEditor} imgEl={rightImg} containerEl={rightContRef.current} /></div>
                        )}
                    </div>
                </div>
            </div>

            <Button size="sm" color="outline-danger"
                style={{ position: 'absolute', top: 42, right: -40 }}
                onClick={onDelete} title="Remove columns block">✕</Button>
        </div>
    );
}

// ─── Parse HTML → blocks ──────────────────────────────────────────────────────
function parseHtmlToBlocks(html) {
    if (!html || !html.trim()) return [{ id: '1', type: 'text', html: '' }];

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const blocks = [];
    let textParts = [];
    let nextId = 1;
    const newId = () => String(nextId++);

    const flushText = () => {
        const h = textParts.join('').trim();
        if (h) blocks.push({ id: newId(), type: 'text', html: h });
        textParts = [];
    };

    for (const node of doc.body.childNodes) {
        const isNewCols = node.nodeType === Node.ELEMENT_NODE && node.tagName === 'DIV' && node.getAttribute('data-type') === 'columns';
        const isOldCols = node.nodeType === Node.ELEMENT_NODE && node.tagName === 'TABLE' && node.getAttribute('data-layout') === 'cols';

        if (isNewCols) {
            flushText();
            const cols = node.querySelectorAll('[data-type="column"]');
            const styleStr = cols[0]?.getAttribute('style') || '';
            const wm = styleStr.match(/width:\s*([\d.]+)%/);
            blocks.push({
                id: newId(), type: 'columns',
                leftPct:   wm ? parseFloat(wm[1]) : 50,
                leftHtml:  cols[0]?.innerHTML?.trim() || '',
                rightHtml: cols[1]?.innerHTML?.trim() || '',
            });
        } else if (isOldCols) {
            // Backward compatibility with old table-based format
            flushText();
            const cells = node.querySelectorAll('td');
            blocks.push({
                id: newId(), type: 'columns',
                leftPct:   parseFloat(cells[0]?.style.width) || 50,
                leftHtml:  cells[0]?.innerHTML?.trim() || '',
                rightHtml: cells[1]?.innerHTML?.trim() || '',
            });
        } else {
            textParts.push(node.outerHTML ?? node.textContent ?? '');
        }
    }
    flushText();

    return blocks.length ? blocks : [{ id: '1', type: 'text', html: '' }];
}

// ─── Serialize blocks → HTML ──────────────────────────────────────────────────
function serializeBlocks(blocks) {
    return blocks.map(b => {
        if (b.type === 'text') return b.html || '';
        if (b.type === 'columns') {
            const l = b.leftPct ?? 50, r = 100 - l;
            return [
                `<div data-type="columns" style="display:flex;gap:12px;margin:8px 0;">`,
                `<div data-type="column" style="width:calc(${l}% - 6px);flex-shrink:0;min-width:0;padding:4px;">${b.leftHtml || '<p></p>'}</div>`,
                `<div data-type="column" style="width:calc(${r}% - 6px);flex-shrink:0;min-width:0;padding:4px;">${b.rightHtml || '<p></p>'}</div>`,
                `</div>`,
            ].join('');
        }
        return '';
    }).join('\n');
}

// ─── Main component ───────────────────────────────────────────────────────────
export function AboutManagement() {
    const [blocks,  setBlocks]  = useState([{ id: '1', type: 'text', html: '' }]);
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [success, setSuccess] = useState(false);
    const [error,   setError]   = useState(null);

    useEffect(() => {
        injectEditorCss();
        document.title = 'Manage About | Admin';
        fetch(ApiEndpoints.ManageAboutPage)
            .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
            .then(d => setBlocks(parseHtmlToBlocks(d.html || '')))
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const updateBlock = (id, changes) =>
        setBlocks(prev => prev.map(b => b.id === id ? { ...b, ...changes } : b));

    const deleteBlock = (id) =>
        setBlocks(prev => prev.length > 1 ? prev.filter(b => b.id !== id) : prev);

    const addBlock = (type) => setBlocks(prev => [...prev, {
        id: Date.now().toString(), type,
        ...(type === 'text'    ? { html: '' }                                  : {}),
        ...(type === 'columns' ? { leftPct: 50, leftHtml: '', rightHtml: '' } : {}),
    }]);

    const handleSave = async () => {
        setSaving(true); setSuccess(false); setError(null);
        try {
            const html = serializeBlocks(blocks);
            const res = await fetch(ApiEndpoints.ManageAboutPage, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ html }),
            });
            if (!res.ok) throw new Error('Failed to save');
            setSuccess(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container">
            <NavMenu />
            <h2 className="mt-3 mb-3 text-center">Edit About Page</h2>

            {error   && <Alert color="danger">{error}</Alert>}
            {success && <Alert color="success">Saved successfully!</Alert>}

            {loading ? <p>Loading...</p> : (
                <>
                    <div style={{ paddingRight: 48 }}>
                        {blocks.map(block =>
                            block.type === 'text'
                                ? <TextBlock
                                    key={block.id}
                                    block={block}
                                    onChange={c => updateBlock(block.id, c)}
                                    onDelete={() => deleteBlock(block.id)}
                                    canDelete={blocks.length > 1}
                                  />
                                : <ColumnsBlock
                                    key={block.id}
                                    block={block}
                                    onChange={c => updateBlock(block.id, c)}
                                    onDelete={() => deleteBlock(block.id)}
                                  />
                        )}
                    </div>

                    <div className="mt-2 d-flex gap-2 flex-wrap align-items-center">
                        <Button color="outline-secondary" size="sm" onClick={() => addBlock('text')}>+ Text block</Button>
                        <Button color="outline-secondary" size="sm" onClick={() => addBlock('columns')}>⊢⊣ Add 2 columns</Button>
                    </div>

                    <div className="mt-3 mb-5">
                        <Button size="lg" color="primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
